import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { Clock, CalendarDays, ChevronDown, Check, Trash2, Timer, Minus, Plus, X } from "lucide-react";
import { PickerOverlay, GRAD } from "../features/calendar/DatePickParts";
import { fmtQuickTime } from "../utils/quickAdd";
import { todayStr } from "../utils/dateHelpers";
import { isTaskOccurringOn } from "../utils/taskLogic";
import { hhmmOf, minutesBetween, sessionTimesError, estimatedTimes, endAfter, DURATION_MAX_MINS } from "../utils/sessionEdit";
import { SESSION_NOTE_MAX } from "../utils/sessionGrouping";
import { HabitIcon } from "./HabitIcon";
import { CategoryIcon } from "./CategoryIcon";
import { useSheetDrag } from "../utils/sheetDrag";

// ─── Session sheet (Jade, 2026-10-11) ─────────────────────────────────────────
// One bottom sheet for both:
//  • mode "edit": fix a logged session's start/end time, or delete it;
//  • mode "add":  log time that wasn't clocked live ("+ Add past session").
// Same look as the Add amount sheet. Nothing is saved until Save; tapping
// outside or swiping it down (it follows the finger, useSheetDrag) closes
// without saving. Rules live in utils/sessionEdit.js.

// Keyframes + the note/search focus rule, added to <head> ONCE (not a <style>
// in the sheet: inserting one restyles the whole page on every open, part of
// the slide's lag — Jade, 2026-10-11). The dim fades in by colour only: it
// contains the sheet, so fading its opacity would fade the sheet too.
const SHEET_CSS = `@keyframes sessionSheetUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}} input.ss-plain:focus{box-shadow:none!important;outline:none!important;border:none!important} @media (pointer: coarse){.ss-for-list{scrollbar-width:none!important;-ms-overflow-style:none} .ss-for-list::-webkit-scrollbar{width:0!important;height:0!important;display:none!important}}`;
// The For list's scrollbar is hidden on touch screens only; a mouse needs it (Jade, 2026-10-26).
if (typeof document !== "undefined" && !document.getElementById("session-sheet-css")) {
  const el = document.createElement("style");
  el.id = "session-sheet-css";
  el.textContent = SHEET_CSS;
  document.head.appendChild(el);
}

const durationLabel = (mins) => {
  if (mins < 1) return "";
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? (m ? `${h} h ${m} min` : `${h} h`) : `${m} min`;
};

const dateLabel = (ds) => {
  if (!ds) return "Pick a date";
  if (ds === todayStr()) return "Today";
  return new Date(ds + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

const valuePill = (active) => ({
  position: "relative", fontSize: 14, fontWeight: 700, padding: "6px 12px", borderRadius: 10, cursor: "pointer",
  color: active ? "var(--c-text-accent)" : "var(--c-text-soft)", background: "var(--c-bg-active)", whiteSpace: "nowrap",
});

function PickRow({ Icon, label, type, value, display, onChange, max }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
      <Icon size={18} color="var(--c-text-soft)" />
      <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>{label}</span>
      <label style={valuePill(!!value)}>
        {display}
        <PickerOverlay type={type} value={value} label={label} onChange={v => onChange(v || "")} {...(max ? { max } : {})} />
      </label>
    </div>
  );
}

// "What was it for?" — an inline list under the row (habits, tasks, categories).
function TargetPicker({ habits, tasks, categories, target, onPick, date }) {
  const [open, setOpen] = useState(!target);
  const [query, setQuery] = useState(""); // search: finds one item in a long list (Jade, 2026-10-11)
  const q = query.trim().toLowerCase();
  const hit = (text) => !q || String(text || "").toLowerCase().includes(q);
  const fHabits = habits.filter(h => hit(h.name));
  // Tasks: only the ones on the session's date (done or not) — listing every
  // task was long and slow (Jade, 2026-10-11). Searching finds any task.
  const fTasks = q ? tasks.filter(t => hit(t.title)) : tasks.filter(t => isTaskOccurringOn(t, date));
  const fCats = categories.filter(c => hit(c.title));
  const nothingHit = !hit("nothing") && !fHabits.length && !fTasks.length && !fCats.length;
  const name = target ? (target.ref.name || target.ref.title) : "Nothing";
  const row = (key, icon, text, t) => {
    const on = target ? (target.kind === t?.kind && target.ref.id === t?.ref.id) : !t;
    return (
      <button key={key} type="button" onClick={() => onPick(t)}
        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", minHeight: 0, padding: "9px 0", border: "none", borderRadius: 0,
          background: "transparent", fontFamily: "inherit", fontSize: 14.5, fontWeight: 700, color: on ? "var(--c-text-accent)" : "var(--c-text)", cursor: "pointer", textAlign: "left" }}>
        {/* 18px icon column + 10px gap, exactly like the Date / Start / End rows below,
            so the names line up with their labels (Jade, 2026-10-11). */}
        {icon && <span style={{ width: 18, display: "flex", justifyContent: "center", flexShrink: 0 }}>{icon}</span>}
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</span>
        {on && <Check size={16} color="var(--c-primary)" />}
      </button>
    );
  };
  const heading = (t) => (
    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: "var(--c-text-soft)", padding: "10px 0 2px" }}>{t}</div>
  );
  return (
    <div>
      <button type="button" onClick={() => setOpen(v => !v)}
        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", minHeight: 44, padding: 0, border: "none", background: "transparent", fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>For</span>
        <span style={{ ...valuePill(!!target), display: "flex", alignItems: "center", gap: 6, maxWidth: "65%" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
          <ChevronDown size={15} style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </span>
      </button>
      {open && (
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search habits, tasks, categories" autoComplete="off" className="ss-plain"
          style={{ width: "100%", boxSizing: "border-box", margin: "2px 0 4px", padding: "9px 12px", borderRadius: 10, border: "none", background: "var(--c-bg-active)", fontFamily: "inherit", fontSize: 14.5, color: "var(--c-text)", outline: "none" }} />
      )}
      {open && (
        // Clear top/bottom lines in the accent, plus a fade at the bottom edge, so
        // it reads as its own scrolling area (Jade, 2026-10-16: it was hard to notice).
        // Side lines too (Jade, 2026-10-17), so it's a full box with 12px inside.
        <div className="ss-for-list" style={{ maxHeight: 230, overflowY: "auto", margin: "2px 0 6px", padding: "2px 12px 0", border: "2px solid color-mix(in srgb, var(--c-primary) 75%, transparent)", borderRadius: 12 }}>
          {hit("nothing") && row("none", <span style={{ color: "var(--c-text-soft)" }}>–</span>, "Nothing", null)}
          {fHabits.length > 0 && heading("Habits")}
          {fHabits.map(h => row(`h${h.id}`, <HabitIcon h={h} size={18} />, h.name, { kind: "habit", ref: h }))}
          {fTasks.length > 0 && heading("Tasks")}
          {/* No leading icon on tasks, and no empty column either: the names start at the left edge (Jade). */}
          {fTasks.map(t => row(`t${t.id}`, null, t.title, { kind: "task", ref: t }))}
          {fCats.length > 0 && heading("Categories")}
          {fCats.map(c => row(`c${c.id}`, <CategoryIcon cat={c} size={16} />, c.title, { kind: "category", ref: c }))}
          {nothingHit && <div style={{ padding: "14px 0", fontSize: 13.5, fontWeight: 600, color: "var(--c-text-soft)" }}>No matches</div>}
          <div aria-hidden style={{ position: "sticky", bottom: 0, height: 22, marginTop: -16, marginLeft: -12, marginRight: -12, pointerEvents: "none", background: "linear-gradient(to bottom, transparent, var(--c-bg-card))" }} />
        </div>
      )}
    </div>
  );
}

// Start & end, or just the length (add mode only). Same sliding-pill switcher
// as the Clock-In assign sheet's tabs.
function HowSwitch({ value, onChange }) {
  const opts = [["times", "Time log"], ["length", "Duration"]];
  const i = opts.findIndex(o => o[0] === value);
  return (
    <div style={{ position: "relative", display: "flex", margin: "6px 0 4px", padding: 3, borderRadius: 12, background: "rgba(120,120,128,0.12)" }}>
      <div style={{ position: "absolute", top: 3, bottom: 3, left: 3, width: "calc(50% - 3px)", borderRadius: 9, background: "var(--c-bg-card)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)", transform: `translateX(${i * 100}%)`, transition: "transform 0.28s cubic-bezier(0.22,1,0.36,1)" }} />
      {opts.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)}
          style={{ position: "relative", flex: 1, minHeight: 0, padding: "7px 0", border: "none", background: "transparent", fontFamily: "inherit",
            fontSize: 13.5, fontWeight: 800, color: value === k ? "var(--c-text-accent)" : "var(--c-text-soft)", cursor: "pointer" }}>
          {label}
        </button>
      ))}
    </div>
  );
}

const LENGTH_QUICK = [15, 30, 45, 60, 90, 120];
const quickLabel = (m) => m < 60 ? `${m}m` : (m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60}h`);

// Type-in hours and minutes (Jade, 2026-10-16: typing beats tapping through
// options). `value` is whole minutes, or 0 for none. The boxes keep what's being
// typed (even an empty box) and follow `value` when it changes from outside
// (a quick pick, or new start/end times in the edit sheet).
function DurationInputs({ value, onChange, steppers = false }) {
  const split = (v) => (v >= 1 ? [String(Math.floor(v / 60)), String(v % 60)] : ["", ""]);
  const [[hs, ms], setText] = useState(() => split(value));
  const typed = (Number(hs) || 0) * 60 + (Number(ms) || 0);
  useEffect(() => {
    if (value !== typed) setText(split(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const edit = (which, raw) => {
    const max = which === "h" ? 23 : 59;
    let digits = raw.replace(/\D/g, "").slice(0, 2);
    if (digits && Number(digits) > max) digits = String(max);
    const next = which === "h" ? [digits, ms] : [hs, digits];
    setText(next);
    onChange((Number(next[0]) || 0) * 60 + (Number(next[1]) || 0));
  };
  const step = (delta) => onChange(Math.max(0, Math.min(DURATION_MAX_MINS, typed + delta)));
  const stepBtn = (Icon, delta, disabled, label) => (
    <button type="button" aria-label={label} disabled={disabled} onClick={() => step(delta)}
      style={{ width: 28, height: 28, minWidth: 0, minHeight: 0, flexShrink: 0, boxSizing: "border-box", padding: 0, borderRadius: "50%", border: "none",
        background: "var(--c-bg-active)", color: "var(--c-text-accent)", display: "flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1 }}>
      <Icon size={15} strokeWidth={2.6} />
    </button>
  );
  const box = (which, text, unit) => (
    <label style={{ display: "flex", alignItems: "baseline", gap: 3, padding: steppers ? "4px 4px" : "6px 10px", borderRadius: 10,
      background: steppers ? "transparent" : "var(--c-bg-active)", cursor: "text" }}>
      <input value={text} onChange={e => edit(which, e.target.value)} inputMode="numeric" placeholder="0" autoComplete="off"
        aria-label={which === "h" ? "Hours" : "Minutes"} className="ss-plain"
        style={{ width: 22, padding: 0, border: "none", borderBottom: steppers ? "1.5px dashed var(--c-border-mid)" : "none", borderRadius: 0,
          background: "transparent", fontFamily: "inherit", fontSize: 15, fontWeight: 800,
          color: steppers ? "var(--c-text)" : "var(--c-text-accent)", textAlign: "center", outline: "none" }} />
      <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--c-text-soft)" }}>{unit}</span>
    </label>
  );
  if (!steppers) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {box("h", hs, "h")}
        {box("m", ms, "min")}
      </div>
    );
  }
  // Add past session: − [h] +  − [m] +, with the numbers typeable (Jade, 2026-10-16).
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
      {stepBtn(Minus, -60, typed < 60, "Less hours")}
      {box("h", hs, "h")}
      {stepBtn(Plus, 60, typed + 60 > DURATION_MAX_MINS, "More hours")}
      <span style={{ width: 8 }} />
      {stepBtn(Minus, -5, typed < 5, "Less minutes")}
      {box("m", ms, "m")}
      {stepBtn(Plus, 5, typed + 5 > DURATION_MAX_MINS, "More minutes")}
    </div>
  );
}

// Add past session → Duration: type it, or tap a quick pick.
function LengthRow({ value, onChange }) {
  return (
    <div>
      {/* Quick picks on top, then the − / + row, both centred (Jade, 2026-10-16). */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, margin: "12px 0 6px" }}>
        {LENGTH_QUICK.map(q => (
          <button key={q} type="button" onClick={() => onChange(q)}
            style={{ flex: "1 1 0", minWidth: 0, minHeight: 0, padding: "10px 0", whiteSpace: "nowrap", borderRadius: 22, border: "none", fontFamily: "inherit", fontSize: 14, fontWeight: 800, cursor: "pointer",
              background: value === q ? "color-mix(in srgb, var(--c-primary) 22%, transparent)" : "var(--c-bg-active)",
              color: value === q ? "var(--c-text-accent)" : "var(--c-text-mid)" }}>
            {quickLabel(q)}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 44 }}>
        <DurationInputs value={value} onChange={onChange} steppers />
      </div>
    </div>
  );
}

export function SessionSheet({ mode = "edit", session = null, title = "", habits = [], tasks = [], categories = [], onSave, onDelete, onClose }) {
  const isAdd = mode === "add";
  const [date, setDate] = useState(isAdd ? todayStr() : session?.date || todayStr());
  const [start, setStart] = useState(isAdd ? "" : hhmmOf(session?.startedAt));
  const [end, setEnd] = useState(isAdd ? "" : hhmmOf(session?.completedAt));
  const [note, setNote] = useState("");
  const [target, setTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Add only: "times" = Start & End, "length" = just how long it was, for when
  // the times are forgotten (Jade, 2026-10-15).
  const [how, setHow] = useState("times");
  const [lenMins, setLenMins] = useState(30);
  const byLength = isAdd && how === "length";
  // Edit, for a session logged by duration only (Jade, 2026-10-16): just Date and
  // Duration, plus an optional "+ Add start & end time". Its stored times were only
  // placed for it, so showing them as fields read as if the user had picked them.
  const wasEstimated = !isAdd && !!session?.estimated;
  const [timesOn, setTimesOn] = useState(!wasEstimated);
  const durOnly = !isAdd && !timesOn;
  const [editLen, setEditLen] = useState(() => session?.minutes || 30);

  const [lenError, setLenError] = useState(null); // edit: a typed duration that runs past midnight
  // Edit: a typed duration keeps the start and moves the end.
  const setLengthKeepStart = (m) => {
    if (!start || m < 1) return;
    const e = endAfter(start, m);
    if (e) { setEnd(e); setLenError(null); } else setLenError("A session stays within one day");
  };
  const placed = byLength ? estimatedTimes(date, lenMins) : durOnly ? estimatedTimes(date, editLen) : null;
  const mins = byLength ? lenMins : durOnly ? editLen : minutesBetween(start, end);
  const error = placed ? (placed.error || null) : lenError || ((start && end) ? sessionTimesError(date, start, end) : null);
  const canSave = placed ? !error : (!!start && !!end && !error);
  const save = () => {
    if (!canSave) return;
    if (byLength) onSave({ target, date, start: placed.start, end: placed.end, note, estimated: true });
    else if (isAdd) onSave({ target, date, start, end, note });
    else if (durOnly) onSave({ start: placed.start, end: placed.end, estimated: true });
    else onSave({ start, end });
  };
  // "+ Add start & end time": start from the times this length is placed at.
  const addTimes = () => {
    const p = estimatedTimes(date, editLen);
    if (!p.error) { setStart(p.start); setEnd(p.end); }
    setLenError(null);
    setTimesOn(true);
  };
  const removeTimes = () => {
    if (mins >= 1) setEditLen(mins);
    setLenError(null);
    setTimesOn(false);
  };
  const sheet = useSheetDrag(onClose);
  const linkBtn = { display: "flex", alignItems: "center", gap: 6, minHeight: 0, margin: "2px 0 0 28px", padding: "6px 0", border: "none", background: "none",
    fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, color: "var(--c-text-accent)", cursor: "pointer" };

  return createPortal(
    <div onClick={sheet.dismiss} className="no-nav-blur" style={{ position: "fixed", inset: 0, zIndex: 100000, display: "flex", alignItems: "flex-end", justifyContent: "center", fontFamily: "'Quicksand', 'Nunito', sans-serif" }}>
      {/* Same motion as Habits > Manage Habits, which Jade found smooth (2026-10-16): the dim
          is just there (no fade in) and the sheet slides up + fades in at once, with no hold and
          no will-change. */}
      <div aria-hidden ref={sheet.dimRef} style={{ position: "absolute", inset: 0, background: "rgba(60,30,50,0.45)" }} />
      <div ref={sheet.sheetRef} onClick={e => e.stopPropagation()} style={{
        position: "relative", width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box", background: "var(--c-bg-card)",
        borderRadius: "32px 32px 0 0", padding: "10px 22px calc(20px + env(safe-area-inset-bottom, 0px))",
        boxShadow: "0 -8px 40px var(--c-shadow)", animation: "sessionSheetUp 0.32s cubic-bezier(0.22,1,0.36,1) both",
      }}>
        <div style={{ width: 40, height: 4, borderRadius: 4, background: "var(--c-border)", margin: "0 auto 14px" }} />
        <div style={{ fontSize: 17, fontWeight: 800, color: "var(--c-text)" }}>{isAdd ? "Add past session" : "Edit session"}</div>
        {!isAdd && title && <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-text-soft)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>}

        <div style={{ marginTop: 12 }}>
          {isAdd && <TargetPicker habits={habits} tasks={tasks} categories={categories} target={target} onPick={setTarget} date={date} />}
          {isAdd
            ? <PickRow Icon={CalendarDays} label="Date" type="date" value={date} display={dateLabel(date)} onChange={v => setDate(v || todayStr())} max={todayStr()} />
            : <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
                <CalendarDays size={18} color="var(--c-text-soft)" />
                <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>Date</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--c-text-mid)", padding: "6px 12px" }}>{dateLabel(date)}</span>
              </div>}
          {isAdd && <HowSwitch value={how} onChange={setHow} />}
          {durOnly && (<>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
              <Timer size={18} color="var(--c-text-soft)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>Duration</span>
              <DurationInputs value={editLen} onChange={setEditLen} />
            </div>
            <button type="button" onClick={addTimes} style={linkBtn}><Plus size={15} strokeWidth={2.6} /> Add start & end time</button>
          </>)}
          {durOnly ? null : byLength
            ? <LengthRow value={lenMins} onChange={setLenMins} />
            : <>
                <PickRow Icon={Clock} label="Start time" type="time" value={start} display={start ? fmtQuickTime(start) : "Set"} onChange={v => { setStart(v); setLenError(null); }} />
                <PickRow Icon={Clock} label="End time" type="time" value={end} display={end ? fmtQuickTime(end) : "Set"} onChange={v => { setEnd(v); setLenError(null); }} />
                {/* Edit: an optional Duration to type instead of an end time. It keeps the
                    start and moves the end (Jade, 2026-10-16). */}
                {!isAdd && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
                    <Timer size={18} color="var(--c-text-soft)" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>Duration</span>
                    <DurationInputs value={Math.max(0, mins)} onChange={setLengthKeepStart} />
                  </div>
                )}
                {wasEstimated && (
                  <button type="button" onClick={removeTimes} style={linkBtn}><X size={15} strokeWidth={2.6} /> Remove start & end time</button>
                )}
              </>}
        </div>

        {/* Length, or why it can't be saved yet. */}
        {/* Duration mode shows nothing here but an error, so it takes no space then
            (Jade, 2026-10-16: it left an empty gap above the note). */}
        {(!byLength || error) && (
          <div style={{ minHeight: 20, marginTop: 4, textAlign: byLength ? "center" : "left", fontSize: 13, fontWeight: 700, color: error ? "#c0392b" : "var(--c-text-accent)" }}>
            {/* Edit shows the length in its Duration row already. */}
            {error || (isAdd && mins >= 1 ? durationLabel(mins) : "")}
          </div>
        )}

        {isAdd && (
          <input value={note} onChange={e => setNote(e.target.value.slice(0, SESSION_NOTE_MAX))} placeholder="Note (optional)" autoComplete="off"
            className="ss-plain"
            style={{ width: "100%", boxSizing: "border-box", marginTop: 8, padding: "11px 14px", borderRadius: 12, border: "none", background: "var(--c-bg-active)", fontFamily: "inherit", fontSize: 14.5, color: "var(--c-text)", outline: "none" }} />
        )}

        <button type="button" disabled={!canSave} onClick={save}
          style={{ width: "100%", marginTop: 16, padding: "13px 0", borderRadius: 14, border: "none", background: GRAD, color: "#fff", fontFamily: "inherit", fontSize: 15, fontWeight: 800,
            cursor: canSave ? "pointer" : "default", opacity: canSave ? 1 : 0.45, transition: "opacity 0.15s" }}>
          {isAdd ? "Add session" : "Save"}
        </button>

        {!isAdd && onDelete && (
          confirmDelete ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginTop: 12, fontSize: 13.5, fontWeight: 700 }}>
              <span style={{ color: "var(--c-text-mid)" }}>Delete this session?</span>
              <button type="button" onClick={onDelete} style={{ border: "none", background: "none", color: "#c0392b", fontFamily: "inherit", fontSize: 13.5, fontWeight: 800, cursor: "pointer", padding: 4, minHeight: 0 }}>Delete</button>
              <button type="button" onClick={() => setConfirmDelete(false)} style={{ border: "none", background: "none", color: "var(--c-text-soft)", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: "pointer", padding: 4, minHeight: 0 }}>Keep</button>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", minHeight: 0, marginTop: 10, padding: "8px 0", border: "none", background: "none", color: "#c0392b", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
              <Trash2 size={15} /> Delete session
            </button>
          )
        )}
      </div>
    </div>,
    document.body
  );
}

// Opens the sheet without re-rendering the page that hosts it (Jade,
// 2026-10-11: the slide felt laggy). The open/closed state lives here, so a
// tap on "+ Add past session" re-renders only this, not the whole Clock-In tab
// (~170ms of the wait at 6x CPU throttle). Call ref.current.open().
export const SessionSheetHost = forwardRef(function SessionSheetHost(props, ref) {
  const [open, setOpen] = useState(false);
  useImperativeHandle(ref, () => ({ open: () => setOpen(true) }), []);
  if (!open) return null;
  return <SessionSheet {...props} onSave={(r) => { props.onSave(r); setOpen(false); }} onClose={() => setOpen(false)} />;
});
